import React, { useState } from 'react';
import { FormDataConsumer, useNotify } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Grid } from '@mui/material';
import { GiftProduct, GiftProductType, GiftRuleType } from '../../../models';
import { ProductList, ProductTypeSelectionModal, ProductEditorModal } from '../components';

const FormProduct: React.FC<{ formData?: any, setProductInfo: (value: any) => void, fuels: any[] }> = ({ formData, setProductInfo, fuels }) => {
  const notify = useNotify();
  const [typeSelectionOpen, setTypeSelectionOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedType, setSelectedType] = useState<GiftProductType | null>(null);
  const [cashbackMode, setCashbackMode] = useState(false);
  const [editingProduct, setEditingProduct] = useState<{ product: GiftProduct; index: number } | null>(null);

  const handleAddProduct = () => {
    setTypeSelectionOpen(true);
  };

  const handleSelectType = (type: GiftProductType, options?: { cashback?: boolean }) => {
    setSelectedType(type);
    setCashbackMode(!!options?.cashback);
    setEditingProduct(null);
    setEditorOpen(true);
  };

  const handleEditProduct = (product: GiftProduct, index: number) => {
    setSelectedType(product.type);
    setCashbackMode((product.cashValue ?? null) !== null);
    setEditingProduct({ product, index });
    setEditorOpen(true);
  };

  return (
    <>
      <FormDataConsumer>
        {({ formData }) => {
          const form = useFormContext();

          // Regra prediction_winner ("Acertou Bolão") + recompensa PredictionTicket
          // ("Direito ao Palpite") = "acertou bolão → ganha palpite em outro bolão",
          // confuso pro cliente. Ocultamos a opção. Validado também no backend (createGift).
          const hasPredictionWinnerRule = (formData.giftRules || []).some(
            (rule: any) => rule?.type === GiftRuleType.prediction_winner,
          );
          // Brinde de Número da Sorte (ou de Giro na Roleta) tem EXATAMENTE uma
          // recompensa: a gifts-api lê só o primeiro produto do brinde e as
          // demais sumiriam em silêncio. Validado também no backend (createGift).
          const currentProducts = formData.products || [];
          const hasDrawingNumberProduct = currentProducts.some(
            (product: any) => product?.type === GiftProductType.DrawingNumber,
          );
          const hasSpinTicketProduct = currentProducts.some(
            (product: any) => product?.type === GiftProductType.SpinTicket,
          );
          // EXCEÇÃO: o brinde de prêmios da roleta (regra "Prêmio da Roleta") é
          // uma caixa de recompensas — cada fatia aponta para UM produto dele e
          // a entrega enxerga um produto só. Por isso ali os tipos se misturam.
          const hasRoletaPrizeRule = (formData.giftRules || []).some(
            (rule: any) => rule?.type === GiftRuleType.roleta_prize,
          );
          const exclusiveRewardTypes = hasRoletaPrizeRule
            ? []
            : [GiftProductType.DrawingNumber, GiftProductType.SpinTicket];
          const excludedProductTypes = [
            ...(hasPredictionWinnerRule ? [GiftProductType.PredictionTicket] : []),
            ...(currentProducts.length > 0 ? exclusiveRewardTypes : []),
          ];

          const hasPointsExchangeRule = (formData.giftRules || []).some(
            (rule: any) => rule?.type === GiftRuleType.points_exchange,
          );

          const handleAdd = () => {
            if (!hasRoletaPrizeRule && hasDrawingNumberProduct) {
              notify('A recompensa "Número da Sorte" é a única do brinde — remova-a para adicionar outra.', { type: 'warning' });
              return;
            }
            if (!hasRoletaPrizeRule && hasSpinTicketProduct) {
              notify('A recompensa "Giro na Roleta" é a única do brinde — remova-a para adicionar outra.', { type: 'warning' });
              return;
            }
            handleAddProduct();
          };

          const handleSaveProduct = (product: GiftProduct) => {
            if (editingProduct !== null) {
              // Edit existing product
              const updatedProducts = [...currentProducts];
              updatedProducts[editingProduct.index] = product;
              form.setValue('products', updatedProducts);
            } else {
              // Add new product
              form.setValue('products', [...currentProducts, product]);
            }

            setEditorOpen(false);
            setEditingProduct(null);
          };

          const handleDeleteProduct = (index: number) => {
            const currentProducts = formData.products || [];
            const updatedProducts = currentProducts.filter((_, i) => i !== index);
            form.setValue('products', updatedProducts);
          };

          return (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <ProductList
                  products={formData.products || []}
                  onAdd={handleAdd}
                  onEdit={handleEditProduct}
                  onDelete={handleDeleteProduct}
                  fuels={fuels}
                />

                <ProductTypeSelectionModal
                  open={typeSelectionOpen}
                  onClose={() => setTypeSelectionOpen(false)}
                  onSelectType={handleSelectType}
                  excludedTypes={excludedProductTypes}
                  hideCashback={hasPointsExchangeRule}
                />

                <ProductEditorModal
                  open={editorOpen}
                  onClose={() => {
                    setEditorOpen(false);
                    setEditingProduct(null);
                  }}
                  onSave={handleSaveProduct}
                  productType={selectedType}
                  cashbackMode={cashbackMode}
                  initialProduct={editingProduct?.product || null}
                  fuels={fuels}
                />
              </Grid>
            </Grid>
          );
        }}
      </FormDataConsumer>
    </>
  );
}

export default FormProduct;
